import React, { useState, useRef, useCallback, useMemo } from 'react';
import Calendar from 'react-calendar';
import { Workout } from '../types';
import { getJSTDateString } from '../utils/dateUtils';
import * as japaneseHolidays from 'japanese-holidays';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarProps {
  selectedDate: Date;
  workouts: Workout[];
  onDateChange: (value: any) => void;
}

const CalendarComponent: React.FC<CalendarProps> = ({
  selectedDate,
  workouts,
  onDateChange,
}) => {
  const { t, language } = useLanguage();
  // currentViewをselectedDateから直接計算するように変更
  const currentView = useMemo(() => {
    const date = new Date(selectedDate);
    date.setDate(1);
    return date;
  }, [selectedDate]);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Get unique dates that have workouts
  const workoutDates = Array.from(new Set(workouts.map(w => w.date)));

  // デバッグ用：ワークアウトデータを確認
  console.log('All workouts:', workouts.length);
  console.log('Workout dates:', workoutDates);
  console.log('Current month view:', currentView.getMonth() + 1, currentView.getFullYear());

  // 曜日の英語表記
  const weekdays = language === 'ja' 
    ? ['日', '月', '火', '水', '木', '金', '土']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // 月の総重量を計算
  const calculateMonthlyTotalWeight = () => {
    const currentMonthWorkouts = workouts.filter(workout => {
      const workoutDate = new Date(workout.date);
      return workoutDate.getMonth() === currentView.getMonth() && 
             workoutDate.getFullYear() === currentView.getFullYear();
    });

    let totalWeight = 0;
    currentMonthWorkouts.forEach(workout => {
      if (workout.weight && workout.weight !== -1 && workout.reps) {
        const weight = typeof workout.weight === 'string' ? parseFloat(workout.weight) : workout.weight;
        const reps = typeof workout.reps === 'string' ? parseInt(workout.reps) : workout.reps;
        if (!isNaN(weight) && !isNaN(reps)) {
          totalWeight += weight * reps;
        }
      }
    });

    return totalWeight;
  };

  // 重量を面白い乗り物例えに変換
  const getWeightComparison = (weight: number) => {
    const comparisons = [
      { threshold: 600000, name: language === 'ja' ? 'スペースシャトル' : 'Space Shuttle', icon: '🚀' },
      { threshold: 500000, name: language === 'ja' ? 'ジャンボジェット機' : 'Jumbo Jet', icon: '✈️' },
      { threshold: 320000, name: language === 'ja' ? '新幹線1両' : 'Bullet Train Car', icon: '🚄' },
      { threshold: 180000, name: language === 'ja' ? '戦闘機' : 'Fighter Jet', icon: '🛩️' },
      { threshold: 100000, name: language === 'ja' ? 'ヘリコプター' : 'Helicopter', icon: '🚁' },
      { threshold: 80000, name: language === 'ja' ? '路線バス' : 'City Bus', icon: '🚌' },
      { threshold: 50000, name: language === 'ja' ? '大型トラック' : 'Large Truck', icon: '🚛' },
      { threshold: 30000, name: language === 'ja' ? '消防車' : 'Fire Truck', icon: '🚒' },
      { threshold: 20000, name: language === 'ja' ? 'キャンピングカー' : 'RV', icon: '🚐' },
      { threshold: 15000, name: language === 'ja' ? 'フォークリフト' : 'Forklift', icon: '🏗️' },
      { threshold: 10000, name: language === 'ja' ? 'スポーツカー' : 'Sports Car', icon: '🏎️' },
      { threshold: 5000, name: language === 'ja' ? '乗用車' : 'Car', icon: '🚗' },
      { threshold: 3000, name: language === 'ja' ? 'ゴルフカート' : 'Golf Cart', icon: '🛺' },
      { threshold: 1500, name: language === 'ja' ? 'スクーター' : 'Scooter', icon: '🛵' },
      { threshold: 500, name: language === 'ja' ? 'バイク' : 'Motorcycle', icon: '🏍️' },
      { threshold: 200, name: language === 'ja' ? '電動自転車' : 'E-bike', icon: '🚴' },
      { threshold: 50, name: language === 'ja' ? '自転車' : 'Bicycle', icon: '🚲' },
      { threshold: 20, name: language === 'ja' ? 'スケートボード' : 'Skateboard', icon: '🛹' },
      { threshold: 0, name: language === 'ja' ? '一輪車' : 'Unicycle', icon: '🦽' }
    ];

    for (const comparison of comparisons) {
      if (weight >= comparison.threshold) {
        const count = Math.floor(weight / comparison.threshold);
        if (count > 1) {
          return `${comparison.icon} ${comparison.name} ${count}${language === 'ja' ? '台分' : ' units'}`;
        }
        return `${comparison.icon} ${comparison.name}${language === 'ja' ? '級' : ' class'}`;
      }
    }
    return `${weight.toFixed(1)}kg`;
  };

  const monthlyTotalWeight = calculateMonthlyTotalWeight();
  const weightComparison = getWeightComparison(monthlyTotalWeight);

  // selectedDateが変更されたときにcurrentViewを同期（useMemoで自動計算されるため削除）



  // スワイプ検出のためのタッチイベントハンドラー
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(false);
    setSwipeDirection(null);
    setSwipeProgress(0);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartX.current) return;
    
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
    
    // 縦方向のスワイプを無視（スクロールを妨げないため）
    const deltaX = Math.abs(touchEndX.current - touchStartX.current);
    const deltaY = Math.abs(touchEndY.current - touchStartY.current);
    
    if (deltaX > deltaY && deltaX > 20) {
      setIsSwiping(true);
      
      // スワイプ方向と進行度を計算
      const direction = touchEndX.current > touchStartX.current ? 'right' : 'left';
      const progress = Math.min(Math.abs(deltaX) / 150, 1); // 最大150pxで100%
      
      setSwipeDirection(direction);
      setSwipeProgress(progress);
      
      e.preventDefault();
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping || !touchStartX.current || !touchEndX.current) {
      touchStartX.current = 0;
      touchEndX.current = 0;
      setSwipeProgress(0);
      setSwipeDirection(null);
      return;
    }

    const deltaX = touchEndX.current - touchStartX.current;
    const minSwipeDistance = 80; // スワイプ距離を増加

    if (Math.abs(deltaX) > minSwipeDistance && swipeProgress > 0.3) {
      setIsAnimating(true);
      
      const newDate = new Date(currentView);
      
      if (deltaX > 0) {
        // 右スワイプ → 前月
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        // 左スワイプ → 翌月
        newDate.setMonth(newDate.getMonth() + 1);
      }
      
      // アニメーション完了後に状態を更新
      setTimeout(() => {
        // 新しい月の同じ日付を選択
        const newSelectedDate = new Date(newDate);
        const maxDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
        const selectedDay = Math.min(selectedDate.getDate(), maxDay);
        newSelectedDate.setDate(selectedDay);
        onDateChange(newSelectedDate);
        setIsAnimating(false);
        setSwipeProgress(0);
        setSwipeDirection(null);
      }, 300);
    } else {
      // スワイプが不十分な場合は元に戻す
      setSwipeProgress(0);
      setSwipeDirection(null);
    }

    // リセット
    touchStartX.current = 0;
    touchEndX.current = 0;
    setIsSwiping(false);
  }, [isSwiping, currentView, swipeProgress, onDateChange, selectedDate]);

  // マウスイベントハンドラー（デスクトップ対応）
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    touchStartX.current = e.clientX;
    touchStartY.current = e.clientY;
    setIsSwiping(false);
    setSwipeDirection(null);
    setSwipeProgress(0);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!touchStartX.current) return;
    
    touchEndX.current = e.clientX;
    touchEndY.current = e.clientY;
    
    // 縦方向のスワイプを無視
    const deltaX = Math.abs(touchEndX.current - touchStartX.current);
    const deltaY = Math.abs(touchEndY.current - touchStartY.current);
    
    if (deltaX > deltaY && deltaX > 20) {
      setIsSwiping(true);
      
      // スワイプ方向と進行度を計算
      const direction = touchEndX.current > touchStartX.current ? 'right' : 'left';
      const progress = Math.min(Math.abs(deltaX) / 150, 1);
      
      setSwipeDirection(direction);
      setSwipeProgress(progress);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isSwiping || !touchStartX.current || !touchEndX.current) {
      touchStartX.current = 0;
      touchEndX.current = 0;
      setSwipeProgress(0);
      setSwipeDirection(null);
      return;
    }

    const deltaX = touchEndX.current - touchStartX.current;
    const minSwipeDistance = 80;

    if (Math.abs(deltaX) > minSwipeDistance && swipeProgress > 0.3) {
      setIsAnimating(true);
      
      const newDate = new Date(currentView);
      
      if (deltaX > 0) {
        // 右スワイプ → 前月
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        // 左スワイプ → 翌月
        newDate.setMonth(newDate.getMonth() + 1);
      }
      
      // アニメーション完了後に状態を更新
      setTimeout(() => {
        // 新しい月の同じ日付を選択
        const newSelectedDate = new Date(newDate);
        const maxDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
        const selectedDay = Math.min(selectedDate.getDate(), maxDay);
        newSelectedDate.setDate(selectedDay);
        onDateChange(newSelectedDate);
        setIsAnimating(false);
        setSwipeProgress(0);
        setSwipeDirection(null);
      }, 300);
    } else {
      // スワイプが不十分な場合は元に戻す
      setSwipeProgress(0);
      setSwipeDirection(null);
    }

    // リセット
    touchStartX.current = 0;
    touchEndX.current = 0;
    setIsSwiping(false);
  }, [isSwiping, currentView, swipeProgress, onDateChange, selectedDate]);

  // 月の移動ハンドラー（useMemoで自動計算されるため削除）
  const handleActiveStartDateChange = useCallback(({ activeStartDate }: { activeStartDate: Date | null }) => {
    if (activeStartDate) {
      // currentViewはuseMemoで自動計算されるため、ここでは何もしない
    }
  }, []);

  // tileClassNameで記録ありの日付に緑丸を付与、今日の日付を強調、土日を色分け
  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      const dateString = getJSTDateString(date);
      const today = getJSTDateString(new Date());
      const selected = getJSTDateString(selectedDate);
      const isToday = dateString === today;
      const isSelected = dateString === selected;
      const hasWorkout = workoutDates.includes(dateString);
      
      // デバッグ用ログ
      if (isToday) {
        console.log('Today detected:', dateString, 'with className:', 'bg-gradient-to-r from-cyan-400 to-blue-500');
      }
      if (hasWorkout) {
        console.log('Workout detected:', dateString, 'should have green circle');
      }
      
      // 土日の判定
      const dayOfWeek = date.getDay();
      const isSunday = dayOfWeek === 0;
      const isSaturday = dayOfWeek === 6;
      
      // 祝日の判定
      const holiday = japaneseHolidays.isHoliday(date);
      const isHoliday = Boolean(holiday);
      
      let className = 'relative';
      
      // 今日の日付のスタイル（最優先）
      if (isToday) {
        className += ' !bg-gradient-to-r !from-cyan-400 !to-blue-500 !text-white !font-bold !shadow-lg';
        className += ' !transform !scale-110 !z-10';
      }
      // 選択された日付のスタイル（2番目に優先）
      else if (isSelected) {
        className += ' bg-blue-500 text-white font-bold shadow-md';
        className += ' transform scale-105 z-5';
      }
      // 記録ありの日付のスタイル（3番目に優先、土日・祝日より優先）
      else if (hasWorkout) {
        className += ' relative react-calendar__tile--has-custom-content'; // tileContentで緑丸を表示するため、元の文字を透明に
      }
      // 土日・祝日の色分け（記録がない場合のみ）
      else if (isSunday || isHoliday) {
        className += ' text-red-500';
      } else if (isSaturday) {
        className += ' text-blue-500';
      }
      
      return className;
    }
    return '';
  };

  // 記録がある日のマーカー表示
  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      const dateString = getJSTDateString(date);
      const hasWorkout = workoutDates.includes(dateString);
      
      if (hasWorkout) {
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-green-500 rounded-full flex items-center justify-center">
              <span className="text-gray-900 font-semibold text-xs">{date.getDate()}</span>
            </div>
          </div>
        );
      }
    }
    return null;
  };

  // Show only day numbers without "日" suffix
  const formatDay = (locale: string | undefined, date: Date) => {
    return date.getDate().toString();
  };

  // スワイプオーバーレイのスタイル
  const getSwipeOverlayStyle = () => {
    if (!isSwiping || !swipeDirection) return {};
    
    const opacity = swipeProgress * 0.3;
    const transform = swipeDirection === 'right' 
      ? `translateX(${swipeProgress * 100}%)` 
      : `translateX(-${swipeProgress * 100}%)`;
    
    return {
      opacity,
      transform,
      transition: 'all 0.1s ease-out'
    };
  };

  // カレンダーコンテナのスタイル
  const getCalendarContainerStyle = () => {
    if (isAnimating) {
      return {
        transform: swipeDirection === 'right' ? 'translateX(-100%)' : 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out'
      };
    }
    return {
      transform: 'translateX(0)',
      transition: 'transform 0.3s ease-in-out'
    };
  };

  // 月移動ボタンのハンドラー
  const handlePrevMonth = useCallback(() => {
    const newDate = new Date(currentView);
    newDate.setMonth(newDate.getMonth() - 1);
    
    const newSelectedDate = new Date(newDate);
    const maxDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
    const selectedDay = Math.min(selectedDate.getDate(), maxDay);
    newSelectedDate.setDate(selectedDay);
    
    onDateChange(newSelectedDate);
  }, [currentView, selectedDate, onDateChange]);

  const handleNextMonth = useCallback(() => {
    const newDate = new Date(currentView);
    newDate.setMonth(newDate.getMonth() + 1);
    
    const newSelectedDate = new Date(newDate);
    const maxDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
    const selectedDay = Math.min(selectedDate.getDate(), maxDay);
    newSelectedDate.setDate(selectedDay);
    
    onDateChange(newSelectedDate);
  }, [currentView, selectedDate, onDateChange]);

  // 今日の日付を取得
  const today = new Date();
  const isCurrentMonth = currentView.getMonth() === today.getMonth() && 
                        currentView.getFullYear() === today.getFullYear();

  return (
    <div className="relative bg-white rounded-lg shadow-lg p-2 overflow-hidden">
      {/* スワイプオーバーレイ */}
      {isSwiping && swipeDirection && (
        <div 
          className={`absolute inset-0 z-10 pointer-events-none ${
            swipeDirection === 'right' ? 'bg-blue-500' : 'bg-green-500'
          } rounded-lg`}
          style={getSwipeOverlayStyle()}
        >
          <div className="flex items-center justify-center h-full">
            <div className="text-white text-lg font-bold">
              {swipeDirection === 'right' ? (
                <div className="flex items-center gap-2">
                  <ChevronLeft size={24} />
                  <span>{language === 'ja' ? '前月' : 'Prev'}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span>{language === 'ja' ? '翌月' : 'Next'}</span>
                  <ChevronRight size={24} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* スワイプ進行度インジケーター */}
      {isSwiping && swipeProgress > 0.1 && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-gray-200 rounded-full h-1 w-16">
            <div 
              className={`h-full rounded-full transition-all duration-100 ${
                swipeDirection === 'right' ? 'bg-blue-500' : 'bg-green-500'
              }`}
              style={{ width: `${swipeProgress * 100}%` }}
            />
          </div>
        </div>
      )}

      <div 
        ref={calendarRef}
        className="w-full"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ touchAction: 'pan-y' }}
      >
        <div className="w-full">
          {/* 月移動ボタンとヘッダー */}
          <div className="flex items-center justify-between py-2 border-b border-gray-200 mb-2">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={language === 'ja' ? '前月' : 'Previous month'}
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            
            <div className="text-lg font-semibold text-gray-800">
              {currentView.toLocaleDateString(language === 'ja' ? 'ja-JP' : 'en-US', {
                year: 'numeric',
                month: 'long'
              })}
            </div>
            
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={language === 'ja' ? '翌月' : 'Next month'}
            >
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
          
          {/* カスタム曜日ヘッダー */}
          <div className="grid grid-cols-7 text-xs font-bold text-gray-700 py-2 border-b border-gray-200">
            {weekdays.map((day, index) => (
              <div 
                key={day}
                className={`text-center ${
                  index === 0 ? 'text-red-500' : 
                  index === 6 ? 'text-blue-500' : 
                  'text-gray-600'
                }`}
              >
                {day}
              </div>
            ))}
          </div>
          <div 
            className="w-full"
            style={getCalendarContainerStyle()}
          >
          <Calendar
            onChange={onDateChange}
            value={selectedDate}
              activeStartDate={currentView}
              onActiveStartDateChange={handleActiveStartDateChange}
            formatDay={formatDay}
            calendarType="gregory"
              locale={language === 'ja' ? 'ja-JP' : 'en-US'}
            tileClassName={tileClassName}
              tileContent={tileContent}
              showNavigation={false}
              className="w-full text-xs min-h-[280px] [&_.react-calendar__month-view__weekdays]:hidden [&_.react-calendar__navigation]:hidden [&_.react-calendar__tile]:p-1 [&_.react-calendar__tile]:m-0 [&_.react-calendar__tile]:h-8 [&_.react-calendar__tile]:w-full [&_.react-calendar__tile]:text-xs [&_.react-calendar__tile--active]:bg-blue-500 [&_.react-calendar__tile--active]:text-white [&_.react-calendar__tile--now]:bg-yellow-100 [&_.react-calendar__tile--now]:border [&_.react-calendar__tile--now]:border-yellow-400 [&_.react-calendar__month-view__days__day]:text-xs [&_.react-calendar__month-view__days__day]:h-8 [&_.react-calendar__month-view__days__day]:w-full [&_.react-calendar__tile--today]:bg-green-100 [&_.react-calendar__tile--today]:border-2 [&_.react-calendar__tile--today]:border-green-400 [&_.react-calendar__tile--today]:font-bold [&_.react-calendar__tile--weekend]:text-red-500 [&_.react-calendar__tile--saturday]:text-blue-500"
          />
          </div>
        </div>
      </div>
      
      {/* 月の総重量表示 */}
      {monthlyTotalWeight > 0 && (
        <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg p-3 mt-3 border border-purple-200">
          <div className="text-center">
            <div className="text-lg font-bold text-purple-800 mb-1">
              {currentView.toLocaleDateString(language === 'ja' ? 'ja-JP' : 'en-US', { month: 'long' })}
              {language === 'ja' ? 'の総重量' : ' Total Weight'}
            </div>
            <div className="text-2xl font-bold text-purple-600 mb-2">
              {monthlyTotalWeight.toLocaleString()}kg
            </div>
            <div className="text-lg text-purple-700 font-medium">
              {weightComparison}
            </div>
          </div>
        </div>
      )}

      {/* 今日の日付インジケーター */}
      {isCurrentMonth && (
        <div className="flex items-center justify-center gap-2 mt-2 text-xs text-gray-600">
          <div className="w-3 h-3 !bg-gradient-to-r !from-cyan-400 !to-blue-500 rounded-full"></div>
          <span>{language === 'ja' ? '今日' : 'Today'}</span>
        </div>
      )}
      
      {/* 記録がある日のインジケーター */}
      <div className="flex items-center justify-center gap-2 mt-1 text-xs text-gray-600">
        <div className="w-4 h-4 border-2 border-green-500 rounded-full"></div>
        <span>{language === 'ja' ? '記録あり（緑丸囲み）' : 'Has Records (Green Circle)'}</span>
      </div>
      
      {/* スワイプヒント */}
      <div className="text-xs text-gray-500 text-center mt-2 opacity-60 flex items-center justify-center gap-2">
        <ChevronLeft size={12} />
        <span>{t('calendar.swipeHint')}</span>
        <ChevronRight size={12} />
      </div>
    </div>
  );
};

export default CalendarComponent;

// tailwindで青丸を付与するカスタムクラスをグローバルCSSに追加する必要あり
// .react-calendar__tile--workout-day { @apply relative after:content-[''] after:absolute after:top-0 after:right-0 after:w-2 after:h-2 after:bg-blue-500 after:rounded-full !important; } 